import React, { FC, ReactNode } from 'react';
import {
    FormControl, FormControlLabel, FormControlProps, FormHelperText, FormLabel, Radio,
    RadioGroup as RadioGroupBase,
    RadioGroupProps as RadioGroupBaseProps,
    StackProps, formLabelClasses,
} from '@mui/material';

import { Typography, TypographyProps } from '../Typography';
import { Stack } from '../Stack';


export interface RadioElement<CType = any> {
    value: string | number;
    label: string | ReactNode;
    disabled?: boolean;
    hide?: boolean | ReactNode;
    Children?: FC<CType>;
    childrenProps?: CType;
}
export interface RadioGroupProps extends RadioGroupBaseProps, Pick<StackProps, 'spacing'>,
    Pick<FormControlProps, 'required' | 'error' | 'fullWidth'> {
    groupLabel?: React.ReactNode;
    labelVariant?: TypographyProps['variant'];
    labelColor?: TypographyProps['color'];
    elements: RadioElement[];
    helper?: ReactNode;
}
export const RadioGroup: FC<RadioGroupProps> = ({
    elements, groupLabel, value, name, spacing = 3, required, helper, error,
    labelVariant = 'regularCaption1', labelColor = 'grayscale.shade90', fullWidth,
    ...props
}) => (
    <FormControl required={required} error={error} fullWidth={fullWidth}>
        {groupLabel && (
            <FormLabel
                id={name}
                sx={({ typography }) => ({
                    ...typography[labelVariant],
                    color: labelColor,
                    [`&.${formLabelClasses.focused}`]: {
                        color: labelColor,
                    },
                })}
            >
                {groupLabel}
            </FormLabel>
        )}
        {helper && (
            <FormHelperText
                sx={({ typography }) => ({
                    ...typography.regularCaption3,
                    mb: 3,
                })}
            >
                {helper}
            </FormHelperText>
        )}
        <RadioGroupBase
            aria-labelledby={name}
            data-testid={`radio-group-${name}`}
            name={name}
            value={value || null}
            {...props}
        >
            <Stack spacing={spacing} mt={groupLabel ? 3 : 0}>
                {elements.map(({
                    label, hide, value, Children, childrenProps, disabled,
                }) => !hide && (
                    <Stack key={value} spacing={2}>
                        <FormControlLabel
                            data-testid={`radio-${value}`}
                            key={value}
                            value={value}
                            disabled={disabled}
                            control={(
                                <Radio
                                    disableRipple
                                    sx={{
                                        p: 0,
                                        borderRadius: '50%',
                                        '&.MuiRadio-root': {
                                            color: theme => theme.palette.grayscale.shade60,
                                            ':hover': {
                                                bgcolor: 'transparent',
                                            },
                                            '& svg:first-of-type': {
                                                transform: 'scale(1.2)',
                                            },
                                        },
                                        '&.Mui-checked': {
                                            color: theme => theme.palette.general.primary50,
                                            '& svg:last-of-type': {
                                                transform: 'scale(1.2)',
                                            },
                                        },
                                        '&.Mui-disabled': {
                                            color: theme => theme.palette.grayscale.shade80,
                                            bgcolor: theme => theme.palette.grayscale.shade20,
                                            boxShadow: 'none',
                                            '& svg:first-of-type': {
                                                color: theme => theme.palette.grayscale.shade20,
                                            },
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: 24,
                                        },
                                    }}
                                />
                            )}
                            label={(
                                <Typography
                                    variant='regularCaption1'
                                    color='grayscale.shade100'
                                    pl={4}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        '& svg': {
                                            verticalAlign: 'middle',
                                        },
                                    }}
                                >
                                    {label}
                                </Typography>
                            )}
                            sx={{
                                mx: 0,
                                '&:hover .MuiRadio-root:not(.Mui-disabled)': {
                                    color: theme => theme.palette.grayscale.shade80,
                                },
                                '&:hover .Mui-checked:not(.Mui-disabled)': {
                                    color: theme => theme.palette.general.primary60,
                                },
                            }}
                        />
                        {Children && <Children {...childrenProps} />}
                    </Stack>
                ))}
            </Stack>
        </RadioGroupBase>
    </FormControl>
);
